import { useState, useCallback, useRef } from 'react';
import { finalizeNestedCampaigns } from '../utils/templateCopy';
import {
  announceOptimisticCampaignCard,
  dropOptimisticCampaignCard,
} from './campaignNesting/optimisticCampaignCard';

/**
 * useAppModals Hook
 * 
 * Manages modal state and handlers for sprint/template creation modals.
 */
export function useAppModals({ 
  handleSprintCreated, 
  handleTemplateCreated, 
  handleSelectSprint, 
  handleSelectTemplate,
  handleNestCampaign,
  handleAddSprint,
}) {
  // Use refs to allow updating the selection handlers after initialization
  // This solves the circular dependency where tab integration needs modals, but modals need tab-aware selection
  const selectSprintRef = useRef(handleSelectSprint);
  const selectTemplateRef = useRef(handleSelectTemplate);
  
  // Setter to update the sprint selection handler (called from App after tab integration is ready)
  const setSelectSprintHandler = useCallback((handler) => {
    if (handler) {
      selectSprintRef.current = handler;
    }
  }, []);

  // Setter to update the template selection handler (called from App after tab integration is ready)
  const setSelectTemplateHandler = useCallback((handler) => {
    if (handler) {
      selectTemplateRef.current = handler;
    }
  }, []);

  // Modal visibility state
  const [isCreateSprintOpen, setIsCreateSprintOpen] = useState(false);
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [preselectedTemplate, setPreselectedTemplate] = useState(null);
  const [parentSprintIdForNesting, setParentSprintIdForNesting] = useState(null);
  // Set when (+) asks for a campaign card on the current board instead of opening it.
  const nestedCreateOptionsRef = useRef(null);
  
  // Settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // ═══════════════════════════════════════════════════════════════
  // SPRINT MODAL
  // ═══════════════════════════════════════════════════════════════

  const openCreateSprintModal = useCallback((template = null, parentSprintId = null) => {
    nestedCreateOptionsRef.current = null;
    setPreselectedTemplate(template);
    setParentSprintIdForNesting(parentSprintId);
    setIsCreateSprintOpen(true);
  }, []);

  const closeCreateSprintModal = useCallback(() => {
    setIsCreateSprintOpen(false);
    setPreselectedTemplate(null);
    setParentSprintIdForNesting(null);
    nestedCreateOptionsRef.current = null;
  }, []);

  // Open modal specifically for creating a nested sprint.
  // options.stayHere keeps the current campaign open and options.connect
  // attaches the new card like the other (+) choices.
  const openCreateNestedSprintModal = useCallback((parentSprintId, options = null) => {
    nestedCreateOptionsRef.current = options;
    setParentSprintIdForNesting(parentSprintId);
    setPreselectedTemplate(null);
    setIsCreateSprintOpen(true);
  }, []);

  // Wrap sprint creation to handle modal close, selection, optional nesting, and nested campaigns from templates
  const onSprintCreated = useCallback(async (newSprint, parentIdForNesting, nestedCampaignsToCreate = []) => {
    const createOptions = nestedCreateOptionsRef.current;
    nestedCreateOptionsRef.current = null;
    const parentId = parentIdForNesting || parentSprintIdForNesting;
    const stayConnected = !!(createOptions?.stayHere && createOptions?.connect?.sourceTaskId);
    const optimisticTaskId = stayConnected ? `task-${Date.now()}` : null;

    // Show the connected card before the campaign API returns. The pill on
    // that card is what spins until nesting finishes.
    if (optimisticTaskId) {
      announceOptimisticCampaignCard({
        sprintId: parentId,
        taskId: optimisticTaskId,
        name: newSprint?.name || 'New campaign',
        icon: newSprint?.icon || null,
        connect: createOptions.connect,
      });
      closeCreateSprintModal();
    }

    let savedSprint;
    try {
      savedSprint = await handleSprintCreated(newSprint);
    } catch (error) {
      dropOptimisticCampaignCard(parentId, optimisticTaskId);
      console.error('[useAppModals] Failed to create campaign', error);
      return;
    }

    if (!optimisticTaskId) closeCreateSprintModal();

    if (!savedSprint?.id) {
      dropOptimisticCampaignCard(parentId, optimisticTaskId);
      console.error('[useAppModals] Root campaign create returned no id — skipping nested create/nest');
      return;
    }
    
    // If we have a parent ID, nest the new sprint under it
    if (parentId && handleNestCampaign) {
      await handleNestCampaign(savedSprint.id, parentId, savedSprint.name, {
        connect: createOptions?.connect || null,
        existingTaskId: optimisticTaskId,
      });
    }
    
    // If there are nested campaigns from a template, create them too
    if (nestedCampaignsToCreate.length > 0 && handleAddSprint) {
      console.log('[useAppModals] Creating nested campaigns from template:', nestedCampaignsToCreate.length);
      
      // Finalize nested campaigns with the root campaign's ID
      const finalizedNested = finalizeNestedCampaigns(nestedCampaignsToCreate, savedSprint.id);
      
      // Create each nested campaign
      for (const nestedCampaign of finalizedNested) {
        try {
          await handleAddSprint(nestedCampaign);
          console.log('[useAppModals] Created nested campaign:', nestedCampaign.name);
        } catch (error) {
          console.error('[useAppModals] Failed to create nested campaign:', error);
        }
      }
    }
    
    // (+) adds a card on the board you are already on.
    if (createOptions?.stayHere) return;

    // Use the ref to get the latest handler (may be tab-aware if available)
    selectSprintRef.current(savedSprint);
  }, [handleSprintCreated, closeCreateSprintModal, parentSprintIdForNesting, handleNestCampaign, handleAddSprint]);

  // ═══════════════════════════════════════════════════════════════
  // TEMPLATE MODAL
  // ═══════════════════════════════════════════════════════════════

  const openCreateTemplateModal = useCallback(() => {
    setIsCreateTemplateOpen(true);
  }, []);

  const closeCreateTemplateModal = useCallback(() => {
    setIsCreateTemplateOpen(false);
  }, []);

  // Wrap template creation to handle modal close and selection
  const onTemplateCreated = useCallback(async (newTemplate) => {
    const savedTemplate = await handleTemplateCreated(newTemplate);
    closeCreateTemplateModal();
    // Use the ref to get the latest handler (may be tab-aware if available)
    selectTemplateRef.current(savedTemplate);
  }, [handleTemplateCreated, closeCreateTemplateModal]);

  // Use template handler (creates a new campaign from template)
  const handleUseTemplate = useCallback((template) => {
    openCreateSprintModal(template);
  }, [openCreateSprintModal]);

  // ═══════════════════════════════════════════════════════════════
  // SETTINGS MODAL
  // ═══════════════════════════════════════════════════════════════

  const openSettingsModal = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  return {
    // Sprint modal
    isCreateSprintOpen,
    preselectedTemplate,
    parentSprintIdForNesting,
    openCreateSprintModal,
    openCreateNestedSprintModal,
    closeCreateSprintModal,
    onSprintCreated,
    setSelectSprintHandler, // Call this after tab integration is ready
    
    // Template modal
    isCreateTemplateOpen,
    openCreateTemplateModal,
    closeCreateTemplateModal,
    onTemplateCreated,
    handleUseTemplate,
    setSelectTemplateHandler, // Call this after tab integration is ready
    
    // Settings modal
    isSettingsOpen,
    openSettingsModal,
    closeSettingsModal,
  };
}

export default useAppModals;
